import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { normalizeText } from '../utils/normalizeText';

/**
 * Dropdown com busca por digitação.
 *
 * Substitui qualquer <select> nativo do projeto. API compatível:
 *   - value          : valor selecionado (string ou number)
 *   - onChange       : callback (newValue) => void
 *   - options        : array de objetos { value, label, disabled?, group? }
 *                      OU array de strings (interpretadas como label=value)
 *   - placeholder    : texto exibido quando nada selecionado (ex.: "Todas as escolas")
 *   - emptyOptionLabel: rótulo da opção "vazia" (value=""). Se omitido, não há opção vazia.
 *                       Use em filtros (ex.: "Todas as escolas") quando aceitar value="".
 *   - required       : se true, não permite limpar a seleção (sem opção vazia explícita)
 *   - disabled       : desabilita o componente
 *   - darkMode       : aplica tema escuro
 *   - className      : classes extras aplicadas no wrapper
 *   - searchPlaceholder: placeholder do campo de busca (default "Buscar...")
 *   - searchThreshold: a partir de quantas opções o campo de busca aparece.
 *                      Abaixo disso a lista inteira já cabe na tela e a caixa
 *                      de busca só atrapalha (default 8). `0` força sempre.
 *   - noResultsLabel : texto exibido quando o filtro não encontra opções
 *   - id, name       : repassados para input hidden (suporta forms)
 *   - autoFocus      : foca o input de busca ao abrir
 *
 * Acessibilidade:
 *   - Setas ↑/↓ navegam, Enter seleciona, Esc fecha, digitar busca.
 *   - O input hidden com `name` permite uso em <form> com required.
 *
 * @example
 *   <SearchableSelect
 *     value={filtroEscola}
 *     onChange={setFiltroEscola}
 *     options={escolas.map(e => ({ value: e.id, label: e.school }))}
 *     emptyOptionLabel="Todas as escolas"
 *     darkMode={darkMode}
 *   />
 */
function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Selecione...',
  emptyOptionLabel,
  required = false,
  disabled = false,
  darkMode = false,
  className = '',
  searchPlaceholder = 'Buscar...',
  searchThreshold = 8,
  noResultsLabel = 'Nenhuma opção encontrada',
  id,
  name,
  autoFocus = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [focusIdx, setFocusIdx] = useState(-1);
  // Posição do painel em coordenadas de viewport: ele é renderizado num
  // portal, fora do fluxo do campo (ver `computePanelPosition`).
  const [panelPos, setPanelPos] = useState(null);
  const wrapperRef = useRef(null);
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  // Normaliza options: aceita array de string ou array de objeto
  const normalizedOptions = useMemo(() => {
    return options.map((opt) => {
      if (typeof opt === 'string' || typeof opt === 'number') {
        return { value: opt, label: String(opt) };
      }
      return {
        value: opt.value,
        label: opt.label ?? String(opt.value),
        disabled: opt.disabled ?? false,
        group: opt.group,
      };
    });
  }, [options]);

  // Lista exibida (com opção vazia se for o caso)
  const fullList = useMemo(() => {
    const list = [...normalizedOptions];
    if (emptyOptionLabel != null && !required) {
      list.unshift({ value: '', label: emptyOptionLabel, isEmpty: true });
    }
    return list;
  }, [normalizedOptions, emptyOptionLabel, required]);

  // Filtra pela busca (label + group), ignorando acento e caixa:
  // quem digita "jose" precisa encontrar "JOSÉ".
  const filtered = useMemo(() => {
    const q = normalizeText(query);
    if (!q) return fullList;
    return fullList.filter((opt) =>
      normalizeText(`${opt.label} ${opt.group || ''}`).includes(q)
    );
  }, [fullList, query]);

  // Texto exibido no botão fechado
  const selectedLabel = useMemo(() => {
    if (value === '' || value == null) {
      return emptyOptionLabel || placeholder;
    }
    const found = normalizedOptions.find(
      (o) => String(o.value) === String(value)
    );
    return found ? found.label : placeholder;
  }, [value, normalizedOptions, emptyOptionLabel, placeholder]);

  const isPlaceholder = value === '' || value == null;

  // Lista curta não ganha caixa de busca: com 3 opções na tela, procurar é
  // olhar. O campo só existe onde ele resolve alguma coisa — e some sozinho,
  // sem cada tela ter de decidir.
  const mostraBusca = fullList.length >= searchThreshold;

  // Fecha ao clicar fora
  useEffect(() => {
    function handleClickOutside(e) {
      const dentroDoCampo = wrapperRef.current?.contains(e.target);
      const dentroDoPainel = panelRef.current?.contains(e.target);
      if (!dentroDoCampo && !dentroDoPainel) {
        setOpen(false);
        setQuery('');
        setFocusIdx(-1);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Foca o input de busca ao abrir
  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  // Sem caixa de busca, uma consulta antiga ficaria filtrando a lista para
  // sempre, sem nada na tela explicando por quê.
  useEffect(() => {
    if (!mostraBusca && query) setQuery('');
  }, [mostraBusca, query]);

  // Reseta foco ao filtrar
  useEffect(() => {
    setFocusIdx(filtered.length > 0 ? 0 : -1);
  }, [query, open, filtered.length]);

  // Faz scroll automático para o item focado
  useEffect(() => {
    if (focusIdx < 0 || !listRef.current) return;
    const item = listRef.current.querySelector(`[data-idx="${focusIdx}"]`);
    if (item && item.scrollIntoView) {
      item.scrollIntoView({ block: 'nearest' });
    }
  }, [focusIdx]);

  // Calcula onde o painel fica em coordenadas de viewport.
  //
  // O painel é `fixed` e mora num portal no <body>. Preso ao campo, ele
  // herdava os limites de quem o hospedava: no modal da Referência Técnica,
  // com `overflow-y-auto` no corpo, a lista era cortada no rodapé; e num
  // campo mais estreito que os 210px mínimos ela vazava para fora da tela.
  //
  // Daqui pra frente a lista decide sozinha: abre para baixo se couber,
  // para cima se não couber, e nunca passa das bordas da tela.
  const computePanelPosition = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margem = 8;
    const vw = window.innerWidth || 0;
    const vh = window.innerHeight || 0;
    const espacoAbaixo = vh - rect.bottom - margem;
    const espacoAcima = rect.top - margem;
    const up = espacoAbaixo < 240 && espacoAcima > espacoAbaixo;

    // A lista parte da largura do campo (nunca menos de 210px: nome de escola
    // não cabe em menos) e CRESCE até caber o rótulo inteiro.
    //
    // Copiar a largura do campo cortava a opção no meio. Num formulário de
    // quatro colunas o campo tem ~155px, e "Substituindo · 1ª aula • 07:00-07:50
    // — Ciências · Célia Nunes" virava "Substituindo · 1ª aula • …" — sumia
    // justamente de quem é o horário, que é a informação que faz a opção
    // existir. O `title` não resolve: quem usa o diário está no tablet, e lá
    // não há mouse para parar em cima.
    //
    // O teto é a tela; os 560px seguram o painel num tamanho legível quando um
    // rótulo é longo demais (aí, sim, o texto corta).
    const minima = Math.max(rect.width, 210);
    const maxima = Math.max(Math.min(vw - margem * 2, 560), minima);

    // Largura real do painel já aberto — é ela que diz se ele passa da borda.
    // Na primeira passada o painel ainda não existe: vale a mínima, e o efeito
    // de reposicionamento corrige assim que ele entra na tela.
    const largura = Math.min(
      Math.max(panelRef.current?.offsetWidth || 0, minima),
      maxima
    );
    let left = rect.left;
    if (left + largura > vw - margem) left = vw - margem - largura;
    if (left < margem) left = margem;

    // Reserva ~64px para a caixa de busca, para o painel inteiro caber no
    // espaço disponível — inclusive em telas estreitas/retrato (ex.: 9:16).
    const disponivel =
      (up ? espacoAcima : espacoAbaixo) - (mostraBusca ? 64 : 8);

    setPanelPos({
      left,
      minWidth: minima,
      maxWidth: maxima,
      maxHeight: Math.max(140, Math.min(288, disponivel)),
      ...(up
        ? { bottom: Math.max(vh - rect.top + 4, margem) }
        : { top: Math.min(rect.bottom + 4, Math.max(vh - margem, 0)) }),
    });
  }, [mostraBusca]);

  // Com o painel fora do fluxo, rolar a página ou o modal deixaria a lista
  // parada no ar longe do campo: reposiciona a cada scroll (inclusive dos
  // containers internos, daí a captura) e a cada resize.
  useEffect(() => {
    if (!open) return;
    computePanelPosition();
    const reposiciona = () => computePanelPosition();
    window.addEventListener('scroll', reposiciona, true);
    window.addEventListener('resize', reposiciona);
    return () => {
      window.removeEventListener('scroll', reposiciona, true);
      window.removeEventListener('resize', reposiciona);
    };
  }, [open, computePanelPosition, filtered.length]);

  function handleToggleOpen() {
    if (disabled) return;
    if (!open) computePanelPosition();
    setOpen((v) => !v);
  }

  function handleSelect(opt) {
    if (opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
    setQuery('');
    setFocusIdx(-1);
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIdx((i) => Math.min(i + 1, filtered.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIdx((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[focusIdx];
      if (opt) handleSelect(opt);
    }
  }

  // Estilos compatíveis com o resto do app
  const buttonBase = darkMode
    ? 'bg-slate-700/50 border-slate-600 text-slate-200 hover:bg-slate-700/70'
    : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100';

  const buttonPlaceholder = darkMode ? 'text-slate-500' : 'text-slate-400';

  const panelBase = darkMode
    ? 'bg-slate-800 border-slate-700 text-slate-200'
    : 'bg-white border-slate-200 text-slate-700';

  const inputBase = darkMode
    ? 'bg-slate-900 border-slate-700 text-slate-200 placeholder-slate-500'
    : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400';

  return (
    <div
      ref={wrapperRef}
      className={`relative min-w-0 ${className}`}
      onKeyDown={handleKeyDown}
    >
      {/* Input hidden para suporte a forms (required, name etc.) */}
      {name && (
        <input
          type="hidden"
          name={name}
          value={value ?? ''}
          required={required}
          readOnly
        />
      )}

      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={handleToggleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${buttonBase} ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        }`}
        title={selectedLabel}
      >
        <span
          className={`truncate text-left flex-1 min-w-0 ${
            isPlaceholder ? buttonPlaceholder : ''
          }`}
        >
          {selectedLabel}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 flex-shrink-0 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && panelPos && createPortal(
        <div
          ref={panelRef}
          className={`fixed rounded-lg border shadow-lg z-[100] ${panelBase}`}
          style={{
            left: panelPos.left,
            // `max-content` é o que deixa o painel crescer até o rótulo
            // inteiro; os limites acima o prendem entre o campo e a tela.
            width: 'max-content',
            minWidth: panelPos.minWidth,
            maxWidth: panelPos.maxWidth,
            ...(panelPos.top != null
              ? { top: panelPos.top }
              : { bottom: panelPos.bottom }),
          }}
        >
          {mostraBusca && (
            <div className="p-2 border-b border-inherit">
              <input
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                autoFocus={autoFocus || true}
                className={`w-full px-3 py-2 rounded-md text-sm border outline-none focus:ring-2 focus:ring-[#0974BA]/40 ${inputBase}`}
              />
            </div>
          )}
          <div
            ref={listRef}
            role="listbox"
            className="overflow-y-auto py-1"
            style={{ maxHeight: panelPos.maxHeight }}
          >
            {filtered.length === 0 ? (
              <div
                className={`px-3 py-3 text-sm text-center ${
                  darkMode ? 'text-slate-500' : 'text-slate-400'
                }`}
              >
                {noResultsLabel}
              </div>
            ) : (
              (() => {
                // Renderiza com cabeçalhos de grupo quando as opções
                // tiverem o campo `group`. Cada vez que muda o grupo,
                // emite um header sticky discreto.
                const elements = [];
                let lastGroup = undefined;
                filtered.forEach((opt, idx) => {
                  if (opt.group !== lastGroup && opt.group) {
                    elements.push(
                      <div
                        key={`group-${idx}`}
                        className={`sticky top-0 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider ${
                          darkMode
                            ? 'bg-slate-800/95 text-slate-500 border-b border-slate-700/50'
                            : 'bg-slate-50/95 text-slate-500 border-b border-slate-200'
                        }`}
                      >
                        {opt.group}
                      </div>
                    );
                    lastGroup = opt.group;
                  }
                  const isSelected = String(opt.value) === String(value);
                  const isFocused = idx === focusIdx;
                  const baseRow =
                    'w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2';
                  const stateClass = opt.disabled
                    ? 'opacity-40 cursor-not-allowed'
                    : isSelected
                      ? darkMode
                        ? 'bg-[#0974BA]/20 text-white'
                        : 'bg-[#0974BA]/10 text-[#0974BA]'
                      : isFocused
                        ? darkMode
                          ? 'bg-slate-700'
                          : 'bg-slate-100'
                        : darkMode
                          ? 'hover:bg-slate-700'
                          : 'hover:bg-slate-100';
                  elements.push(
                    <button
                      key={`${opt.value}-${idx}`}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      data-idx={idx}
                      disabled={opt.disabled}
                      onClick={() => handleSelect(opt)}
                      onMouseEnter={() => setFocusIdx(idx)}
                      className={`${baseRow} ${stateClass}`}
                      title={opt.label}
                    >
                      <span className="truncate flex-1">{opt.label}</span>
                      {isSelected && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </button>
                  );
                });
                return elements;
              })()
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default SearchableSelect;
